import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

interface PhotoLightboxProps {
  photos: Array<{ id: string; url: string }>;
  index: number | null;
  onClose: () => void;
  onIndexChange: (i: number) => void;
}

export function PhotoLightbox({
  photos,
  index,
  onClose,
  onIndexChange,
}: PhotoLightboxProps) {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
  }, [index]);

  useEffect(() => {
    if (index === null) return;
    function handle(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") {
        onIndexChange(index! > 0 ? index! - 1 : photos.length - 1);
      } else if (e.key === "ArrowRight") {
        onIndexChange(index! < photos.length - 1 ? index! + 1 : 0);
      }
    }
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [index, photos.length, onIndexChange]);

  if (index === null || !photos[index]) return null;
  const hasNav = photos.length > 1;

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent
        showCloseButton={false}
        className="max-w-7xl w-auto p-0 bg-black/95 border-0 overflow-hidden"
      >
        <DialogTitle className="sr-only">
          Photo {index + 1} of {photos.length}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Use arrow keys to navigate
        </DialogDescription>
        <div className="relative flex items-center justify-center min-h-[400px] max-h-[90vh]">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="size-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
          )}
          <img
            src={photos[index].url}
            alt=""
            className={`max-h-[90vh] max-w-full object-contain transition-opacity ${loading ? "opacity-0" : "opacity-100"}`}
            onLoad={() => setLoading(false)}
            onError={() => setLoading(false)}
          />
          <button
            onClick={onClose}
            className="absolute top-3 right-3 size-8 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
            aria-label="Close"
          >
            <X className="size-4" />
          </button>
          {hasNav && (
            <>
              <button
                onClick={() =>
                  onIndexChange(index > 0 ? index - 1 : photos.length - 1)
                }
                className="absolute left-3 top-1/2 -translate-y-1/2 size-10 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                aria-label="Previous"
              >
                <ChevronLeft className="size-5" />
              </button>
              <button
                onClick={() =>
                  onIndexChange(index < photos.length - 1 ? index + 1 : 0)
                }
                className="absolute right-3 top-1/2 -translate-y-1/2 size-10 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                aria-label="Next"
              >
                <ChevronRight className="size-5" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
                {index + 1} / {photos.length}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
